import React, { useState } from "react";
import { X, User, KeyRound, Palette } from "lucide-react";

const formatApiError = (e) =>
  e?.response?.data?.detail || e?.response?.data?.message || e?.message || "";

export const ProfileModal = ({ user, onClose, updateProfile, changePassword, appearance, showToast }) => {
  const [tab, setTab] = useState("account");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Edit profile"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ background: "rgba(11,18,20,0.45)" }}
      data-testid="profile-modal"
    >
      <div className="w-full max-w-md" style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-modal)" }}>
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>Your profile</h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="profile-modal-close"
            className="p-1 rounded"
            style={{ color: "var(--ink-tertiary)" }}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pb-2 flex gap-2 border-b" style={{ borderColor: "var(--border-default)" }}>
          <TabButton active={tab === "account"} onClick={() => setTab("account")} testId="profile-tab-account" icon={<User className="h-4 w-4" />}>
            Account info
          </TabButton>
          <TabButton active={tab === "password"} onClick={() => setTab("password")} testId="profile-tab-password" icon={<KeyRound className="h-4 w-4" />}>
            Password
          </TabButton>
          {appearance && (
            <TabButton active={tab === "appearance"} onClick={() => setTab("appearance")} testId="profile-tab-appearance" icon={<Palette className="h-4 w-4" />}>
              Appearance
            </TabButton>
          )}
        </div>

        <div className="px-6 py-4">
          {tab === "account" && (
            <AccountForm user={user} updateProfile={updateProfile} showToast={showToast} onClose={onClose} />
          )}
          {tab === "password" && (
            <PasswordForm changePassword={changePassword} showToast={showToast} onClose={onClose} />
          )}
          {tab === "appearance" && appearance && (
            <AppearanceForm appearance={appearance} showToast={showToast} />
          )}
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, testId, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${
      active
        ? "border-[#185FA5] text-[#185FA5]"
        : "border-transparent text-slate-600 hover:text-slate-900"
    }`}
  >
    {icon}
    {children}
  </button>
);

const AccountForm = ({ user, updateProfile, showToast, onClose }) => {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const emailChanged = email.trim().toLowerCase() !== (user.email || "").toLowerCase();
  const dirty =
    name.trim() !== (user.name || "") ||
    emailChanged ||
    phone.trim() !== (user.phone || "");

  const submit = async () => {
    setErr("");
    if (!name.trim()) { setErr("Name cannot be empty."); return; }
    if (!email.trim()) { setErr("Email cannot be empty."); return; }
    if (emailChanged && !currentPassword) {
      setErr("Enter your current password to change your email.");
      return;
    }
    setBusy(true);
    try {
      const patch = {};
      if (name.trim() !== (user.name || "")) patch.name = name.trim();
      if (emailChanged) {
        patch.email = email.trim();
        patch.current_password = currentPassword;
      }
      if (phone.trim() !== (user.phone || "")) patch.phone = phone.trim();
      await updateProfile(patch);
      showToast?.("Profile updated.");
      onClose();
    } catch (e) {
      setErr(formatApiError(e) || "Could not update profile.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="profile-account-form">
      <Field label="Name">
        <input
          data-testid="profile-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Email">
        <input
          data-testid="profile-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Phone">
        <input
          data-testid="profile-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 555-5555"
          className={inputCls}
        />
      </Field>
      {emailChanged && (
        <Field label="Current password (required to change email)">
          <input
            data-testid="profile-current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={inputCls}
            autoComplete="current-password"
          />
        </Field>
      )}
      {err && <p className="text-sm font-medium text-rose-700" data-testid="profile-account-error">{err}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          data-testid="profile-account-cancel"
          className="px-4 py-2 rounded-md border bg-white text-sm font-medium hover:border-[#605E5C] disabled:opacity-50"
          style={{ borderColor: "#E1DFDD", color: "#201F1E" }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !dirty}
          data-testid="profile-account-save"
          className="px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          style={{ background: "#185FA5" }}
        >
          {busy ? "Saving\u2026" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

const PasswordForm = ({ changePassword, showToast, onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!current) { setErr("Enter your current password."); return; }
    if (next.length < 6) { setErr("New password must be at least 6 characters."); return; }
    if (next !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      showToast?.("Password updated.");
      onClose();
    } catch (e) {
      setErr(formatApiError(e) || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="profile-password-form">
      <Field label="Current password">
        <input
          data-testid="profile-pw-current"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className={inputCls}
          autoComplete="current-password"
        />
      </Field>
      <Field label="New password (at least 6 characters)">
        <input
          data-testid="profile-pw-new"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className={inputCls}
          autoComplete="new-password"
        />
      </Field>
      <Field label="Confirm new password">
        <input
          data-testid="profile-pw-confirm"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={inputCls}
          autoComplete="new-password"
        />
      </Field>
      {err && <p className="text-sm font-medium text-rose-700" data-testid="profile-password-error">{err}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          data-testid="profile-pw-cancel"
          className="px-4 py-2 rounded-md border bg-white text-sm font-medium hover:border-[#605E5C] disabled:opacity-50"
          style={{ borderColor: "#E1DFDD", color: "#201F1E" }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="profile-pw-save"
          className="px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          style={{ background: "#185FA5" }}
        >
          {busy ? "Saving\u2026" : "Update password"}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "#605E5C" }}>
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-md border bg-white px-3 py-2 text-base focus:outline-none focus:border-[#185FA5] focus:ring-2 focus:ring-[#185FA5]/25 border-slate-300";


const OptionRow = ({ label, description, value, options, onChange, testId }) => (
  <div className="mb-5" data-testid={testId}>
    <div className="flex items-baseline justify-between mb-1.5">
      <span className="text-sm font-semibold" style={{ color: "var(--ink-primary)" }}>{label}</span>
      <span className="text-xs" style={{ color: "var(--ink-tertiary)" }}>{options.find((o) => o.value === value)?.label}</span>
    </div>
    {description && (
      <p className="text-xs mb-2" style={{ color: "var(--ink-tertiary)" }}>{description}</p>
    )}
    <div className="flex gap-2" role="radiogroup" aria-label={label}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`${testId}-${opt.value}`}
            onClick={() => onChange(opt.value)}
            className="flex-1 px-3 py-2 text-sm font-semibold focus:outline-none"
            style={{
              background: active ? "var(--brand-primary)" : "var(--surface-card)",
              color: active ? "var(--ink-on-brand)" : "var(--ink-primary)",
              border: `1px solid ${active ? "var(--brand-primary)" : "var(--border-default)"}`,
              borderRadius: "var(--radius-md)",
              transition: "background-color var(--duration-base) var(--ease-standard), color var(--duration-base) var(--ease-standard), border-color var(--duration-base) var(--ease-standard)",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

const AppearanceForm = ({ appearance, showToast }) => {
  const { theme, setTheme, contrast, setContrast } = appearance;
  return (
    <div data-testid="profile-appearance-form">
      <OptionRow
        label="Theme"
        description="Light works best in daylight. Dark is easier on the eyes at night."
        value={theme}
        options={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }]}
        onChange={(v) => { setTheme(v); showToast?.(`Theme set to ${v}`); }}
        testId="appearance-theme"
      />
      <OptionRow
        label="Contrast"
        description="High contrast strengthens borders and text for easier reading in bright rooms."
        value={contrast}
        options={[{ value: "standard", label: "Standard" }, { value: "high", label: "High contrast" }]}
        onChange={(v) => { setContrast(v); showToast?.(`Contrast set to ${v === "high" ? "high" : "standard"}`); }}
        testId="appearance-contrast"
      />
      <p className="text-xs" style={{ color: "var(--ink-tertiary)" }}>
        Your choice is remembered on this device. If the app is installed on multiple devices, set it separately on each.
      </p>
    </div>
  );
};
